import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ClipboardList, ChevronLeft, ChevronRight, User, Phone, Check, Sparkles } from "lucide-react";
import { WhatsAppIcon } from "./Brand";
import { waLink } from "../../lib/site";
import { useLead, getSessionId } from "../../hooks/useLead";
import { Reveal, Eyebrow } from "./Motion";
import { trackLead, trackContact } from "../../utils/pixelEvents";

const EASE = [0.22, 1, 0.36, 1];

const STEPS = [
  {
    id: 1,
    question: "Qual área você deseja depilar?",
    subtitle: "Selecione a região de seu interesse",
    options: [
      { label: "Virilha Completa", badge: "Oferta R$ 49,90" },
      { label: "Axilas" },
      { label: "Pernas Inteiras" },
      { label: "Meia Perna" },
      { label: "Buço ou Rosto" },
      { label: "Outra área corporal" },
    ],
  },
  {
    id: 2,
    question: "Já realizou depilação a laser antes?",
    subtitle: "Isso nos ajuda a personalizar sua avaliação",
    options: [
      { label: "Não, será minha primeira vez" },
      { label: "Sim, já fiz sessões de laser antes" },
      { label: "Costumo usar apenas lâmina ou cera" },
    ],
  },
  {
    id: 3,
    question: "Qual período do dia é melhor para você?",
    subtitle: "Vamos verificar a disponibilidade da agenda",
    options: [
      { label: "Manhã", detail: "08h às 12h" },
      { label: "Tarde", detail: "12h às 18h" },
      { label: "Noite", detail: "18h às 21h" },
      { label: "Qualquer horário comercial", detail: "Mais flexível" },
    ],
  },
];

// Constrói a mensagem personalizada do WhatsApp
const buildWaMessage = ({ area, first_time, preferred_time, name }) => {
  const lines = [
    `Olá! Preenchi o formulário no site do *Laser Day* da Aisla Studio.`,
    ``,
    `📋 *Resumo do meu interesse:*`,
    `• *Área:* ${area || "A definir"}`,
    `• *Experiência prévia:* ${first_time || "Não informada"}`,
    `• *Horário preferido:* ${preferred_time || "A combinar"}`,
    ``,
    `Meu nome é: *${name || ""}*`,
    `Gostaria de confirmar os valores e a disponibilidade! ✨`,
  ];
  return lines.join("\n");
};

export const QuizSection = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState({
    area: "",
    first_time: "",
    preferred_time: "",
  });
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { saveLead, trackEvent, resetSession } = useLead();
  const phoneInputRef = useRef(null);

  const totalSteps = 4; // 3 perguntas + 1 etapa de contato
  const progressPct = Math.round((currentStep / totalSteps) * 100);

  // Formatação automática do telefone (99) 99999-9999
  const handlePhoneChange = (e) => {
    let digits = e.target.value.replace(/\D/g, "").slice(0, 11);
    let formatted = digits;
    if (digits.length > 2 && digits.length <= 7) {
      formatted = `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    } else if (digits.length > 7) {
      formatted = `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    }
    setPhone(formatted);
    if (digits.length >= 10) setPhoneError("");
  };

  const getActiveAnswer = () => {
    if (currentStep === 1) return answers.area;
    if (currentStep === 2) return answers.first_time;
    if (currentStep === 3) return answers.preferred_time;
    return "";
  };

  const handleSelectOption = (value) => {
    let updated;
    if (currentStep === 1) {
      updated = { ...answers, area: value };
      saveLead({ step: 1, area: value, cta_origin: "embedded_form" });
      trackEvent(1, "select", value);
    } else if (currentStep === 2) {
      updated = { ...answers, first_time: value };
      saveLead({ step: 2, first_time: value, cta_origin: "embedded_form" });
      trackEvent(2, "select", value);
    } else if (currentStep === 3) {
      updated = { ...answers, preferred_time: value };
      saveLead({ step: 3, preferred_time: value, cta_origin: "embedded_form" });
      trackEvent(3, "select", value);
    }
    setAnswers(updated);
  };

  const handleNext = () => {
    if (currentStep < 3) {
      const next = currentStep + 1;
      setCurrentStep(next);
      trackEvent(next, "view");
    } else if (currentStep === 3) {
      setCurrentStep(4);
      trackEvent(4, "view");
      setTimeout(() => phoneInputRef.current?.focus(), 300);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((s) => s - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) {
      setPhoneError("Por favor, informe seu número de WhatsApp com DDD.");
      return;
    }

    setIsSubmitting(true);

    const leadPayload = {
      step: 4,
      area: answers.area || "A definir",
      first_time: answers.first_time || "",
      preferred_time: answers.preferred_time || "",
      name: name.trim() || "Cliente",
      phone: digits,
      completed: true,
      cta_origin: "embedded_form",
    };

    const currentSessionId = getSessionId();
    await saveLead(leadPayload);
    await trackEvent(4, "submit", answers.area);

    // Dispara evento de conversão principal no Meta Pixel com deduplicação
    trackLead(currentSessionId);

    resetSession();

    setSubmitted(true);
    setIsSubmitting(false);

    // Redireciona para o WhatsApp
    const msg = buildWaMessage({
      area: answers.area,
      first_time: answers.first_time,
      preferred_time: answers.preferred_time,
      name: name.trim(),
    });

    setTimeout(() => {
      window.open(waLink(msg), "_blank", "noopener,noreferrer");
    }, 400);
  };

  const handleRestart = () => {
    resetSession();
    setCurrentStep(1);
    setAnswers({ area: "", first_time: "", preferred_time: "" });
    setName("");
    setPhone("");
    setPhoneError("");
    setSubmitted(false);
  };

  const currentQuestionData = STEPS[currentStep - 1];
  const activeAnswer = getActiveAnswer();

  return (
    <section
      id="agendamento"
      className="relative overflow-hidden py-24 sm:py-32"
      aria-labelledby="quiz-section-title"
      data-testid="quiz-embedded-section"
    >
      {/* Glow suave e partículas de fundo */}
      <div
        className="pointer-events-none absolute left-1/2 top-10 -translate-x-1/2 h-[550px] w-[550px] rounded-full bg-[#E9B3A0]/20 blur-[130px]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute right-10 bottom-10 h-[350px] w-[350px] rounded-full bg-[#B8654F]/15 blur-[100px]"
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
        {/* Cabeçalho persuasivo da seção */}
        <div className="mx-auto max-w-2xl text-center">
          <Reveal>
            <div className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FAF5EE] px-4 py-1.5 ring-1 ring-[#E9C4B6]/50 shadow-xs mb-3">
              <ClipboardList className="h-4 w-4 text-[#9A4A38]" />
              <span className="text-xs font-bold uppercase tracking-wider text-[#9A4A38]">
                Simulação Rápida & Atendimento
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="font-script text-4xl sm:text-5xl text-[#9A4A38] leading-tight">
              Formulário de
            </p>
            <h2
              id="quiz-section-title"
              className="mt-1 font-display text-3xl font-black uppercase tracking-tight text-[#2D1C18] sm:text-5xl"
            >
              ORÇAMENTO & AGENDAMENTO
            </h2>
          </Reveal>

          <Reveal delay={0.2}>
            <p className="mt-3 text-sm sm:text-base text-[#6B5650] font-medium">
              Leva menos de <strong className="text-[#2D1C18] font-bold">40 segundos</strong>. Sem compromisso.
            </p>
          </Reveal>
        </div>

        {/* Card do Formulário */}
        <div className="mx-auto mt-10 max-w-xl">
          <Reveal delay={0.25}>
            <div className="relative overflow-hidden rounded-[2rem] bg-white p-6 shadow-xl ring-1 ring-[#E9C4B6]/40 sm:p-10">
              {submitted ? (
                /* Tela de Sucesso */
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, ease: EASE }}
                  className="py-6 text-center"
                >
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-[#1E7E45] shadow-inner">
                    <Check className="h-8 w-8 stroke-[2.5]" />
                  </div>
                  <h3 className="font-display text-2xl font-black text-[#2D1C18]">
                    Tudo pronto{name ? `, ${name.split(" ")[0]}` : ""}!
                  </h3>
                  <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-[#6B5650]">
                    Seu atendimento exclusivo foi gerado. Se a conversa no WhatsApp não abriu automaticamente, clique no botão abaixo:
                  </p>

                  <div className="mt-6 flex flex-col gap-3">
                    <a
                      href={waLink(
                        buildWaMessage({
                          area: answers.area,
                          first_time: answers.first_time,
                          preferred_time: answers.preferred_time,
                          name,
                        })
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackContact()}
                      className="btn-rose w-full justify-center py-3.5 text-base shadow-lg"
                    >
                      <WhatsAppIcon className="h-5 w-5" /> Abrir conversa no WhatsApp
                    </a>

                    <button
                      type="button"
                      onClick={handleRestart}
                      className="text-xs font-semibold text-[#9A7A72] hover:text-[#2D1C18] transition-colors py-2"
                    >
                      Refazer simulação com outra área
                    </button>
                  </div>
                </motion.div>
              ) : (
                /* Fluxo em Etapas */
                <div>
                  {/* Barra de Progresso Superior */}
                  <div className="mb-6">
                    <div className="mb-2 flex items-center justify-between text-xs font-bold">
                      <span className="text-[#9A4A38] uppercase tracking-wider">
                        Pergunta {currentStep} de {totalSteps}
                      </span>
                      <span className="text-[#6B5650] tabular-nums font-semibold">
                        {progressPct}%
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-[#FAF5EE]">
                      <motion.div
                        className="h-full rounded-full bg-gradient-to-r from-[#A9573F] to-[#E4A08B]"
                        initial={{ width: 0 }}
                        animate={{ width: `${progressPct}%` }}
                        transition={{ duration: 0.4, ease: EASE }}
                      />
                    </div>
                  </div>

                  {/* Conteúdo Dinâmico com Animação */}
                  <AnimatePresence mode="wait">
                    {currentStep <= 3 ? (
                      /* Etapas de Perguntas (1, 2, 3) */
                      <motion.div
                        key={currentStep}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3, ease: EASE }}
                      >
                        <h3 className="font-display text-xl font-bold text-[#2D1C18] sm:text-2xl">
                          {currentQuestionData.question}{" "}
                          <span className="text-[#9A4A38] font-bold">*</span>
                        </h3>
                        <p className="mt-1 text-xs text-[#9A7A72]">
                          {currentQuestionData.subtitle}
                        </p>

                        {/* Lista de Opções Estilo Radio */}
                        <div className="mt-6 space-y-3">
                          {currentQuestionData.options.map((opt) => {
                            const isSelected = activeAnswer === opt.label;
                            return (
                              <button
                                key={opt.label}
                                type="button"
                                onClick={() => handleSelectOption(opt.label)}
                                className={`group flex w-full items-center justify-between rounded-2xl border p-4 text-left transition-all duration-200 cursor-pointer ${
                                  isSelected
                                    ? "border-[#9A4A38] bg-[#FAF5EE] shadow-sm ring-1 ring-[#9A4A38]/30"
                                    : "border-[#E9C4B6]/60 bg-white hover:border-[#9A4A38]/50 hover:bg-[#FAF5EE]/30"
                                }`}
                              >
                                <div className="flex items-center gap-3.5">
                                  {/* Círculo do Radio */}
                                  <div
                                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-all ${
                                      isSelected
                                        ? "border-[#9A4A38] bg-[#9A4A38] text-white"
                                        : "border-[#D1B5AD] bg-white group-hover:border-[#9A4A38]"
                                    }`}
                                  >
                                    {isSelected && (
                                      <div className="h-2 w-2 rounded-full bg-white" />
                                    )}
                                  </div>

                                  <span className="text-sm font-semibold text-[#2D1C18] sm:text-base">
                                    {opt.label}
                                  </span>
                                </div>

                                {/* Badge ou Detalhe Extra */}
                                {opt.badge && (
                                  <span className="rounded-full bg-[#E9B3A0]/30 px-2.5 py-0.5 text-[11px] font-bold text-[#9A4A38]">
                                    {opt.badge}
                                  </span>
                                )}
                                {opt.detail && (
                                  <span className="text-xs text-[#9A7A72]">
                                    {opt.detail}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </motion.div>
                    ) : (
                      /* Etapa 4: Contato (Nome e Telefone Obrigatório) */
                      <motion.div
                        key="contact"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3, ease: EASE }}
                      >
                        <h3 className="font-display text-xl font-bold text-[#2D1C18] sm:text-2xl">
                          Onde podemos enviar seu atendimento?{" "}
                          <span className="text-[#9A4A38] font-bold">*</span>
                        </h3>
                        <p className="mt-1 text-xs text-[#9A7A72]">
                          Informe seu nome e WhatsApp para receber o orçamento e garantir sua vaga.
                        </p>

                        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-[#2D1C18]">
                              Seu Nome
                            </label>
                            <div className="relative mt-1.5">
                              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-[#9A7A72]">
                                <User className="h-4 w-4" />
                              </span>
                              <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Ex: Mariana Silva"
                                className="w-full rounded-xl border border-[#E9C4B6] bg-[#FAF5EE]/40 py-2.5 pl-10 pr-3 text-sm text-[#2D1C18] outline-none transition-all placeholder:text-[#9A7A72]/60 focus:border-[#9A4A38] focus:bg-white focus:ring-2 focus:ring-[#9A4A38]/20"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-[#2D1C18]">
                              WhatsApp <span className="text-red-600">* (Obrigatório)</span>
                            </label>
                            <div className="relative mt-1.5">
                              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-[#9A7A72]">
                                <Phone className="h-4 w-4" />
                              </span>
                              <input
                                ref={phoneInputRef}
                                type="tel"
                                value={phone}
                                onChange={handlePhoneChange}
                                placeholder="(11) 99999-9999"
                                className={`w-full rounded-xl border py-2.5 pl-10 pr-3 text-sm text-[#2D1C18] outline-none transition-all placeholder:text-[#9A7A72]/60 focus:bg-white focus:ring-2 ${
                                  phoneError
                                    ? "border-red-400 bg-red-50/50 focus:border-red-500 focus:ring-red-200"
                                    : "border-[#E9C4B6] bg-[#FAF5EE]/40 focus:border-[#9A4A38] focus:ring-[#9A4A38]/20"
                                }`}
                                required
                              />
                            </div>
                            {phoneError && (
                              <p className="mt-1 text-xs text-red-600 font-medium">
                                {phoneError}
                              </p>
                            )}
                          </div>

                          <div className="pt-2">
                            <button
                              type="submit"
                              disabled={isSubmitting}
                              className="btn-rose w-full justify-center py-3.5 text-sm font-bold shadow-md cursor-pointer hover:shadow-lg transition-all"
                            >
                              <WhatsAppIcon className="h-5 w-5" />
                              <span>{isSubmitting ? "Enviando..." : "Finalizar e Receber no WhatsApp"}</span>
                            </button>
                            <p className="mt-2 text-center text-[11px] text-[#9A7A72]">
                              🔒 Seus dados estão seguros e não enviamos spam.
                            </p>
                          </div>
                        </form>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Botões de Navegação Inferiores (Apenas etapas 1 a 3) */}
                  {currentStep <= 3 && (
                    <div className="mt-8 flex items-center justify-between border-t border-[#E9C4B6]/30 pt-5">
                      <button
                        type="button"
                        onClick={handleBack}
                        disabled={currentStep === 1}
                        className={`flex items-center gap-1 text-xs font-bold transition-all ${
                          currentStep === 1
                            ? "invisible text-transparent"
                            : "text-[#6B5650] hover:text-[#2D1C18] cursor-pointer"
                        }`}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        <span>Voltar</span>
                      </button>

                      <button
                        type="button"
                        onClick={handleNext}
                        disabled={!activeAnswer}
                        className={`flex items-center gap-1.5 rounded-full px-5 py-2.5 text-xs font-bold transition-all cursor-pointer ${
                          activeAnswer
                            ? "bg-[#9A4A38] text-white shadow-sm hover:bg-[#833827] active:scale-95"
                            : "bg-[#E9C4B6]/40 text-[#9A7A72] cursor-not-allowed"
                        }`}
                      >
                        <span>Próxima</span>
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
